import { Badge } from '@/components/ui/badge';
import { MapPin, Flag, PenTool as Trowel, Scroll, Search, Compass, Hammer, Sparkles, Footprints, Flame, Lightbulb, Map } from 'lucide-react';
import { motion } from 'framer-motion';
import { STEP_TYPE_LABELS } from './guided-study-constants';

interface Props {
    guidedStudy: GuidedStudy;
    currentStep: GuidedStudyStep;
    currentStepIndex: number;
    currentSubStepIndex: number;
    totalSteps: number;
    totalSubSteps: number;
    totalSubStepsInStudy: number;
    progressPercent: number;
    maxProgressPercent: number;
    isCompleted: boolean;
    maxReachedStepIndex: number;
    maxReachedSubStepIndex: number;
    isDarkMode: boolean;
    getSubStepCount: (step: GuidedStudyStep) => number;
    onJumpTo: (stepIndex: number, subStepIndex: number) => void;
    hoverInfo: { x: number; title: string; stepNum: number; isReached: boolean } | null;
    setHoverInfo: (info: { x: number; title: string; stepNum: number; isReached: boolean } | null) => void;
}

const STEP_TYPE_ICONS: Record<string, any> = {
    'read': Scroll,
    'observe': Search,
    'context': Compass,
    'method': Trowel,
    'reflect': Sparkles,
    'interpret': Lightbulb,
    'apply': Footprints,
    'pray': Flame,
    'custom': MapPin
};

export function GuidedStudyProgressBar({
    guidedStudy,
    currentStep,
    currentStepIndex,
    currentSubStepIndex,
    totalSteps,
    totalSubSteps,
    totalSubStepsInStudy,
    progressPercent,
    maxProgressPercent,
    isCompleted,
    maxReachedStepIndex,
    maxReachedSubStepIndex,
    isDarkMode,
    getSubStepCount,
    onJumpTo,
    hoverInfo,
    setHoverInfo
}: Props) {
    return (
        <>
            {/* Step/Substep Info Row */}
            <div className={`flex flex-wrap items-center gap-y-2 gap-x-3 text-[11px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-amber-500/60' : 'text-primary/60'} `}>
                <div className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>Discovery {currentStepIndex + 1} of {totalSteps}</span>
                </div>
                {totalSubSteps > 1 && (
                    <div className="flex items-center gap-2">
                        <span className="opacity-30">/</span>
                        <div className="flex items-center gap-1.5">
                            <Flag className="h-3.5 w-3.5" />
                            <span>Point {currentSubStepIndex + 1} of {totalSubSteps}</span>
                        </div>
                    </div>
                )}
                <span className="opacity-60 hidden sm:inline">•</span>
                <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={`text-[10px] uppercase tracking-wider font-bold h-5 px-1.5 ${isDarkMode ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-amber-100 text-amber-700 border-amber-200'} `}>
                        {STEP_TYPE_LABELS[currentStep.type]}
                    </Badge>
                    {currentStep.optional && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-muted-foreground uppercase">
                            Optional
                        </Badge>
                    )}
                </div>
            </div>

            {/* Progress Bar (Clickable) */}
            <div
                className="mb-4 py-3 group relative cursor-pointer"
                onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percent = x / rect.width;

                    const subStepMap: { stepIndex: number; subStepIndex: number; title: string }[] = [];
                    for (let sIdx = 0; sIdx < guidedStudy.steps.length; sIdx++) {
                        const step = guidedStudy.steps[sIdx];
                        const subStepsInStep = getSubStepCount(step);
                        for (let ssIdx = 0; ssIdx < subStepsInStep; ssIdx++) {
                            subStepMap.push({
                                stepIndex: sIdx,
                                subStepIndex: ssIdx,
                                title: step.title
                            });
                        }
                    }

                    const targetIdx = Math.min(Math.floor(percent * subStepMap.length), subStepMap.length - 1);
                    const target = subStepMap[targetIdx];
                    setHoverInfo({
                        x,
                        title: target.title,
                        stepNum: target.stepIndex + 1,
                        isReached: isCompleted ||
                            target.stepIndex < maxReachedStepIndex ||
                            (target.stepIndex === maxReachedStepIndex && target.subStepIndex <= maxReachedSubStepIndex)
                    });
                }}
                onMouseLeave={() => setHoverInfo(null)}
                onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percent = x / rect.width;

                    const subStepMap: { stepIndex: number; subStepIndex: number }[] = [];
                    for (let sIdx = 0; sIdx < guidedStudy.steps.length; sIdx++) {
                        const step = guidedStudy.steps[sIdx];
                        const subStepsCount = getSubStepCount(step);
                        for (let ssIdx = 0; ssIdx < subStepsCount; ssIdx++) {
                            subStepMap.push({ stepIndex: sIdx, subStepIndex: ssIdx });
                        }
                    }

                    const targetIdx = Math.min(Math.floor(percent * subStepMap.length), subStepMap.length - 1);
                    const target = subStepMap[targetIdx];

                    const isReached = isCompleted ||
                        target.stepIndex < maxReachedStepIndex ||
                        (target.stepIndex === maxReachedStepIndex && target.subStepIndex <= maxReachedSubStepIndex);

                    if (isReached) {
                        onJumpTo(target.stepIndex, target.subStepIndex);
                    } else {
                        onJumpTo(maxReachedStepIndex, maxReachedSubStepIndex);
                    }
                }}
            >
                <div className="relative w-full h-2.5">
                    {/* The Bar Background */}
                    <div className={`absolute inset-0 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-900/50' : 'bg-slate-100'} `}>
                        {/* Max reached indicator */}
                        {!isCompleted && maxReachedStepIndex !== undefined && (
                            <div
                                className={`absolute top-0 left-0 h-full pointer-events-none transition-all duration-500 ${isDarkMode ? 'bg-amber-500/10' : 'bg-primary/5'}`}
                                style={{ width: `${maxProgressPercent}% ` }}
                            />
                        )}

                        {/* Current progress bar (active portion) - The Expedition Path */}
                        <div
                            className={`absolute top-0 left-0 h-full transition-all duration-500 ease-in-out ${isDarkMode ? 'bg-amber-500/80 shadow-[0_0_15px_rgba(245,158,11,0.3)]' : 'bg-primary/80 shadow-[0_0_10px_rgba(var(--primary),_0.2)]'} `}
                            style={{ width: `${isCompleted ? 100 : progressPercent}% ` }}
                        >
                            {/* Dashed trail effect inside the active path */}
                            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(255,255,255,0.4) 50%)', backgroundSize: '8px 100%' }} />
                        </div>
                    </div>

                    {/* Current position marker (glow tip) - Outside overflow-hidden */}
                    {!isCompleted && progressPercent > 0 && (
                        <div
                            className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-background shadow-[0_0_12px_rgba(var(--primary),_0.6)] z-20 transition-all duration-500 ${isDarkMode ? 'bg-amber-400' : 'bg-primary'} `}
                            style={{ left: `${progressPercent}% `, transform: 'translate(-50%, -50%)' }}
                        />
                    )}

                    {/* Step Dividers/Markers - Outside overflow-hidden */}
                    {!isCompleted && (
                        <div className="absolute inset-0 flex pointer-events-none">
                            {(() => {
                                const segments: number[] = [];
                                let currentSum = 0;
                                for (let i = 0; i < totalSteps - 1; i++) {
                                    const step = guidedStudy.steps[i];
                                    const subStepsCount = getSubStepCount(step);
                                    currentSum += subStepsCount;
                                    segments.push((currentSum / totalSubStepsInStudy) * 100);
                                }
                                return segments.map((pos, i) => {
                                    const nextStepType = guidedStudy.steps[i + 1]?.type || 'custom';
                                    const Icon = STEP_TYPE_ICONS[nextStepType] || MapPin;
                                    const isReached = maxProgressPercent >= pos;

                                    return (
                                        <div
                                            key={i}
                                            className={`absolute h-full w-[2px] z-10 ${isDarkMode ? 'text-background' : 'text-white'} `}
                                            style={{ left: `${pos}% ` }}
                                        >
                                            <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 p-1.5 rounded-full border-2 shadow-md transition-all duration-300 ${(isReached)
                                                ? (isDarkMode ? 'bg-amber-500 border-amber-600 text-amber-950 scale-110' : 'bg-primary border-primary text-primary-foreground scale-110')
                                                : (isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-500 opacity-60' : 'bg-white border-slate-200 text-slate-400 opacity-60')
                                                } `}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    )}
                </div>
            </div>

            {/* Floating Hover Tooltip */}
            {hoverInfo && (
                <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-full mb-2 z-50 pointer-events-none"
                    style={{ left: hoverInfo.x }}
                >
                    <div className={`px-2 py-1 rounded shadow-xl text-[10px] whitespace-nowrap -translate-x-1/2 flex flex-col items-center border ${isDarkMode ? 'bg-[#1a1a1b] border-amber-900/30' : 'bg-white border-amber-100'
                        } `}>
                        <span className={`font-bold uppercase tracking-tight ${isDarkMode ? 'text-amber-500' : 'text-primary'} `}>Discovery {hoverInfo.stepNum}</span>
                        <span className="opacity-80 text-[9px] font-medium">{hoverInfo.title}</span>
                        {!hoverInfo.isReached && (
                            <span className="text-orange-500 font-bold uppercase text-[8px] mt-0.5 tracking-widest">Locked Area</span>
                        )}
                    </div>
                    <div className={`w-2 h-2 rotate-45 -mt-1 mx-auto border-r border-b ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                        } `} />
                </motion.div>
            )}
        </>
    );
}
